import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Trash2, Check, X, Database, RefreshCw, BookOpen, Megaphone, Users, ClipboardList, Wrench, Film, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SignupAlertsToggle from "@/components/SignupAlertsToggle";
import FounderDropCard from "@/components/FounderDropCard";
import JingleCard from "@/components/JingleCard";
import BrochuresCard from "@/components/BrochuresCard";
import AdsReviewCard from "@/components/AdsReviewCard";
import OwnerAdLauncherCard from "@/components/OwnerAdLauncherCard";
import OwnerProduceCard from "@/components/OwnerProduceCard";
import AdsTxtSyncCard from "@/components/AdsTxtSyncCard";
import GiveawayCard from "@/components/GiveawayCard";
import OwnerToolsCard from "@/components/OwnerToolsCard";
import ChaptersCard from "@/components/ChaptersCard";
import AdminUsersCard from "@/components/AdminUsersCard";
import AdminPressCard from "@/components/AdminPressCard";
import AICallersCard from "@/components/AICallersCard";
import FixoInsightsCard from "@/components/FixoInsightsCard";
import { ShowoInsightsCard, ShowoMakeInsightsCard } from "@/components/ShowoInsightsCards";

function authHeaders() {
  const t = localStorage.getItem("fliboe_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const TABS = [
  { id: "content",   label: "Content",       icon: BookOpen,      desc: "Fliboe Flip, AI shows, jingles, founder drop, brochures" },
  { id: "growth",    label: "Users & Growth", icon: Users,         desc: "User search, signup alerts, giveaways, press" },
  { id: "ads",       label: "Ads & Money",   icon: Megaphone,     desc: "Owner ad launcher, review queue, ads.txt, owner tools" },
  { id: "fixo",      label: "Fixo Insights", icon: Wrench,        desc: "Fixo signups, triage volume, top zips, contact form inbox" },
  { id: "showo",     label: "Showo Insights", icon: Film,         desc: "Films, plays, tips, top categories, creators, and recent uploads" },
  { id: "showo_make",label: "Showo Make",     icon: Sparkles,     desc: "Movie renders, success rate, face uploads, credits burned, failures" },
  { id: "moderation",label: "Moderation",    icon: ClipboardList, desc: "User reports and backups" },
];

export default function Admin() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState(() => localStorage.getItem("fliboe_admin_tab") || "content");
  const [reports, setReports] = useState([]);
  const [status, setStatus] = useState("open");
  const [backups, setBackups] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const r = await api.get(`/admin/reports?status=${status}`, { headers: authHeaders() });
      setReports(r.data || []);
      const b = await api.get("/admin/backups", { headers: authHeaders() });
      setBackups(b.data || []);
    } catch (_) {}
  };
  useEffect(() => { if (user?.role === "admin" && tab === "moderation") load(); }, [user, status, tab]);
  useEffect(() => { localStorage.setItem("fliboe_admin_tab", tab); }, [tab]);

  if (loading) return <div className="max-w-5xl mx-auto px-5 py-20 text-slate-500 fl-mono text-xs uppercase tracking-widest">// Authenticating…</div>;
  if (!user || user.role !== "admin") {
    return (
      <main className="max-w-md mx-auto px-5 py-32 text-center" data-testid="admin-denied">
        <Shield className="w-12 h-12 text-emerald-500 mx-auto" />
        <h1 className="fl-display text-3xl font-bold mt-4">Admin only</h1>
        <p className="text-slate-400 mt-2">You don't have access to this area.</p>
        <Link to="/" className="fl-btn ghost mt-6 inline-flex">Back home</Link>
      </main>
    );
  }

  const act = async (reportId, action) => {
    if (action !== "dismiss" && !window.confirm(`Confirm: ${action.replace("_", " ")}?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/reports/${reportId}`, { action }, { headers: authHeaders() });
      await load();
    } catch (e) { setMsg("Action failed"); }
    setBusy(false);
  };

  const snapshot = async () => {
    setBusy(true); setMsg("Snapshotting…");
    try {
      const { data } = await api.post("/admin/backup", {}, { headers: authHeaders() });
      setMsg(`Backup ${data.snap_id} created (${data.counts.users}u · ${data.counts.stations}s · ${data.counts.tracks}t).`);
      await load();
    } catch (e) { setMsg("Backup failed"); }
    setBusy(false);
  };

  return (
    <main className="max-w-6xl mx-auto px-5 py-12" data-testid="admin-page">
      <div className="flex items-center gap-3 mb-2">
        <Shield className="w-6 h-6 text-emerald-500" />
        <div className="fl-mono text-[10px] tracking-[0.3em] uppercase text-emerald-500">// Admin control</div>
      </div>
      <h1 className="fl-display text-4xl font-extrabold tracking-tighter">Admin</h1>
      {msg && <div className="mt-3 text-xs text-emerald-400">{msg}</div>}

      {/* Sub-app quick nav — jump to Astro / Fixo / Showo / Casino from here */}
      <div className="mt-4 flex flex-wrap gap-2" data-testid="admin-subapps">
        <a href="/astro" target="_blank" rel="noreferrer" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="admin-go-astro">Open Astro</a>
        <a href="/fixo" target="_blank" rel="noreferrer" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="admin-go-fixo">Open Fixo</a>
        <a href="/showo" target="_blank" rel="noreferrer" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="admin-go-showo">Open Showo</a>
        <a href="/showo/studio" target="_blank" rel="noreferrer" className="fl-btn !py-1.5 !px-3 text-xs !bg-pink-500 !text-black" data-testid="admin-go-showo-studio">Showo Studio</a>
        <a href="/ampliboe" className="fl-btn !py-1.5 !px-3 text-xs !bg-fuchsia-500 !text-black" data-testid="admin-go-ampliboe">🚀 Ampliboe</a>
        <a href="/casino" target="_blank" rel="noreferrer" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="admin-go-casino">Open Casino</a>
        <a href="/pulse" target="_blank" rel="noreferrer" className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="admin-go-pulse">Open Pulse</a>
      </div>

      {/* Tab bar */}
      <nav className="mt-6 flex flex-wrap gap-2 border-b border-white/5 pb-3" data-testid="admin-tabs">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition ${
              tab === id ? "bg-emerald-500 text-black" : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white"
            }`}
            data-testid={`admin-tab-${id}`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </nav>
      <p className="text-white/40 text-xs mt-2 fl-mono uppercase tracking-widest" data-testid="admin-tab-desc">
        {TABS.find(t => t.id === tab)?.desc}
      </p>

      {/* Content tab */}
      {tab === "content" && (
        <div className="mt-4" data-testid="admin-panel-content">
          <ChaptersCard />
          <OwnerProduceCard />
          <AICallersCard />
          <FounderDropCard />
          <JingleCard />
          <BrochuresCard />
        </div>
      )}

      {/* Users & Growth tab */}
      {tab === "growth" && (
        <div className="mt-4" data-testid="admin-panel-growth">
          <AdminUsersCard />
          <SignupAlertsToggle />
          <GiveawayCard />
          <AdminPressCard />
        </div>
      )}

      {/* Ads & Money tab */}
      {tab === "ads" && (
        <div className="mt-4" data-testid="admin-panel-ads">
          <OwnerAdLauncherCard />
          <AdsReviewCard />
          <AdsTxtSyncCard />
          <OwnerToolsCard />
        </div>
      )}

      {/* Fixo Insights tab */}
      {tab === "fixo" && (
        <div className="mt-4" data-testid="admin-panel-fixo">
          <FixoInsightsCard />
        </div>
      )}

      {/* Showo Insights tab */}
      {tab === "showo" && (
        <div className="mt-4" data-testid="admin-panel-showo">
          <ShowoInsightsCard />
        </div>
      )}

      {/* Showo Make Insights tab */}
      {tab === "showo_make" && (
        <div className="mt-4" data-testid="admin-panel-showo-make">
          <ShowoMakeInsightsCard />
        </div>
      )}

      {/* Moderation tab — reports + backups */}
      {tab === "moderation" && (
        <div className="mt-4" data-testid="admin-panel-moderation">
          {/* Reports */}
          <section className="mt-4">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <h2 className="fl-display text-2xl font-bold">User reports</h2>
              <div className="flex gap-2">
                {["open", "resolved", "dismissed"].map(s => (
                  <button key={s} onClick={() => setStatus(s)} className={`fl-pill ${status === s ? "is-active" : ""}`} data-testid={`admin-status-${s}`}>
                    {s}
                  </button>
                ))}
                <button onClick={load} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid="admin-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            {reports.length === 0 ? (
              <div className="fl-card p-8 text-center text-slate-500 fl-mono text-xs uppercase tracking-widest">// No {status} reports</div>
            ) : (
              <div className="space-y-3">
                {reports.map(r => (
                  <div key={r.report_id} className="fl-card p-4" data-testid={`report-${r.report_id}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 text-xs">
                          <span className="fl-mono text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full bg-red-500/15 border border-red-500/40 text-red-300">{r.category}</span>
                          <span className="text-slate-500 fl-mono text-[10px]">{r.report_id} · {(r.created_at || "").slice(0, 16).replace("T", " ")}</span>
                        </div>
                        <div className="mt-2 text-sm text-white whitespace-pre-wrap">{r.reason}</div>
                        <div className="mt-2 text-xs text-slate-400">
                          Station: <code className="text-emerald-400">{r.station_id || "-"}</code>{" · "}
                          Track: <code className="text-emerald-400">{r.track_id || "-"}</code>{" · "}
                          Reporter country: {r.reporter_ip_hint}
                        </div>
                      </div>
                      {r.status === "open" && (
                        <div className="flex flex-wrap gap-2 shrink-0">
                          {r.track_id && (
                            <button onClick={() => act(r.report_id, "remove_track")} disabled={busy} className="fl-btn !py-1.5 !px-3 text-xs bg-red-500 hover:bg-red-400" data-testid={`act-remove-track-${r.report_id}`}>
                              <Trash2 className="w-3.5 h-3.5" /> Remove track
                            </button>
                          )}
                          {r.station_id && (
                            <button onClick={() => act(r.report_id, "remove_station")} disabled={busy} className="fl-btn !py-1.5 !px-3 text-xs bg-red-500 hover:bg-red-400" data-testid={`act-remove-station-${r.report_id}`}>
                              <Trash2 className="w-3.5 h-3.5" /> Remove station
                            </button>
                          )}
                          <button onClick={() => act(r.report_id, "resolve")} disabled={busy} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid={`act-resolve-${r.report_id}`}>
                            <Check className="w-3.5 h-3.5" /> Resolve
                          </button>
                          <button onClick={() => act(r.report_id, "dismiss")} disabled={busy} className="fl-btn ghost !py-1.5 !px-3 text-xs" data-testid={`act-dismiss-${r.report_id}`}>
                            <X className="w-3.5 h-3.5" /> Dismiss
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Backups */}
          <section className="mt-12">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
              <h2 className="fl-display text-2xl font-bold flex items-center gap-2"><Database className="w-5 h-5 text-emerald-500" /> Backups</h2>
              <button onClick={snapshot} disabled={busy} className="fl-btn !py-2 !px-3 text-sm" data-testid="admin-snapshot">
                <Database className="w-4 h-4" /> Snapshot now
              </button>
            </div>
            {backups.length === 0 ? (
              <div className="fl-card p-8 text-center text-slate-500 fl-mono text-xs uppercase tracking-widest">// No backups yet — click "Snapshot now"</div>
            ) : (
              <div className="fl-card divide-y divide-white/5">
                {backups.map(b => (
                  <div key={b.snap_id} className="p-4 flex items-center justify-between gap-3 text-sm">
                    <div>
                      <code className="text-emerald-400 font-mono">{b.snap_id}</code>
                      <span className="text-slate-500 ml-2 fl-mono text-xs">{(b.created_at || "").slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <div className="text-xs text-slate-400 fl-mono">
                      {b.counts?.users || 0} users · {b.counts?.stations || 0} stations · {b.counts?.tracks || 0} tracks · {b.counts?.waitlist || 0} waitlist
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="mt-3 text-xs text-slate-500 fl-mono tracking-widest uppercase">// Latest 14 snapshots retained · stored in MongoDB</p>
          </section>
        </div>
      )}
    </main>
  );
}
